import { createSelector } from 'reselect';
import type { ProformaReqTypeFormAddRequest } from '@atlassian/jira-proforma-request-type-config/src/services/add-proforma-rtc/types.tsx';
import type { ProformaReqTypeFormRemovalRequest } from '@atlassian/jira-proforma-request-type-config/src/services/remove-proforma-rtc/types.tsx';
import type { ProformaRequestTypeFormCardConfig } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types.tsx';
import type { ActiveLayoutItem } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types/layout-items.tsx';
import type { LinkedField, State, ProformaReqTypeConfigState } from './types.tsx';

export const getProformaReqTypeConfig = (state: State): ProformaReqTypeConfigState =>
	state.proformaReqTypeConfig;

export const getCloudId = (state: State): string => state.context.cloudId;

export const getExtraDefinerId = (state: State): number | null => state.context.extraDefinerId;

export const getProjectId = (state: State): number => state.context.projectId;

export const getProjectType = (state: State): string => state.context.projectType;

export const getFormMarkedForRemoval = (state: State): boolean =>
	state.proformaReqTypeConfig.associatedForm?.markedForRemoval ?? false;

export const getProformaFormName = createSelector(
	getProformaReqTypeConfig,
	getFormMarkedForRemoval,
	(
		proformaReqTypeConfig: ProformaReqTypeConfigState,
		isAssociatedFormMarkedForRemoval: boolean,
	): string | undefined => {
		const optimisticFormFormName = proformaReqTypeConfig.optimisticNewForm?.formName;

		const associatedProformaFormName = isAssociatedFormMarkedForRemoval
			? undefined
			: proformaReqTypeConfig.associatedForm?.formName;

		return associatedProformaFormName || optimisticFormFormName;
	},
);

export const getProformaFormId = createSelector(
	getProformaReqTypeConfig,
	getFormMarkedForRemoval,
	(
		proformaReqTypeConfig: ProformaReqTypeConfigState,
		isAssociatedFormMarkedForRemoval: boolean,
	): number | undefined => {
		const optimisticFormFormId = proformaReqTypeConfig.optimisticNewForm?.formId;

		const associatedProformaFormId = isAssociatedFormMarkedForRemoval
			? undefined
			: proformaReqTypeConfig.associatedForm?.formId;

		return associatedProformaFormId || optimisticFormFormId;
	},
);

export const getProformaFormLinkedFields = createSelector(
	getProformaReqTypeConfig,
	getFormMarkedForRemoval,
	(
		proformaReqTypeConfig: ProformaReqTypeConfigState,
		isAssociatedFormMarkedForRemoval: boolean,
	): string[] | undefined => {
		const optimisticFormLinkedFields = proformaReqTypeConfig.optimisticNewForm?.linkedFields
			? Object.keys(proformaReqTypeConfig.optimisticNewForm.linkedFields)
			: undefined;

		const associatedProformaLinkedFields = isAssociatedFormMarkedForRemoval
			? undefined
			: proformaReqTypeConfig.associatedForm?.linkedFieldsKey;
		return associatedProformaLinkedFields || optimisticFormLinkedFields;
	},
);

export const isProformaFormUsedAsIssueForm = createSelector(
	getProformaReqTypeConfig,
	getFormMarkedForRemoval,
	(
		proformaReqTypeConfig: ProformaReqTypeConfigState,
		isAssociatedFormMarkedForRemoval: boolean,
	): boolean => {
		const optimisticFormUsedAsIssueForm = proformaReqTypeConfig.optimisticNewForm?.usedAsIssueForm;

		const associatedFormUsedAsIssueForm = isAssociatedFormMarkedForRemoval
			? undefined
			: proformaReqTypeConfig.associatedForm?.usedAsIssueForm;

		return (
			(optimisticFormUsedAsIssueForm !== undefined && optimisticFormUsedAsIssueForm) ||
			(associatedFormUsedAsIssueForm !== undefined && associatedFormUsedAsIssueForm)
		);
	},
);

export const getProformaRequestTypeFormCardConfig = createSelector(
	getProformaReqTypeConfig,
	getProjectId,
	getExtraDefinerId,
	(
		proformaReqTypeConfig,
		projectId,
		extraDefinerId,
	): ProformaRequestTypeFormCardConfig | undefined => {
		const hasRequestTypeId = extraDefinerId !== null;

		if (!proformaReqTypeConfig) {
			return undefined;
		}
		const isAssociatedFormMarkedForRemoval = proformaReqTypeConfig.associatedForm?.markedForRemoval;
		if (hasRequestTypeId) {
			/**
			 * For 2 cases, we want to return optimisticNewForm:
			 * 1. If RTC page has associatedForm, user removed and selected optimistic form
			 *  - in this case, proformaReqTypeConfig.associatedForm?.markedForRemoval will be true
			 * 2. If RTC page has no associatedForm, and user selected new form using Empty state add form component
			 *
			 */
			const form =
				proformaReqTypeConfig?.optimisticNewForm ??
				(!isAssociatedFormMarkedForRemoval ? proformaReqTypeConfig?.associatedForm : undefined);

			if (form) {
				return {
					formId: form.formId,
					formName: form.formName,
					linkedFields: 'linkedFields' in form ? form.linkedFields : undefined,
					isOptimistic: !!proformaReqTypeConfig?.optimisticNewForm,
					requestTypeId: extraDefinerId,
					projectId,
					restrictionStatus: form.restrictionStatus,
				};
			}
		}

		return undefined;
	},
);

export const isProformaReqTypeConfigDirty = createSelector(
	getProformaReqTypeConfig,
	(proformaReqTypeConfig: ProformaReqTypeConfigState): boolean =>
		(proformaReqTypeConfig.optimisticNewForm && true) ||
		proformaReqTypeConfig.associatedForm?.markedForRemoval ||
		false,
);

export const proformaReqTypeFormRemovalRequest = createSelector(
	getCloudId,
	getProjectId,
	getExtraDefinerId,
	getProformaReqTypeConfig,
	(
		cloudId,
		projectId,
		extraDefinerId,
		proformaReqTypeConfig,
	): ProformaReqTypeFormRemovalRequest | undefined => {
		const formId = proformaReqTypeConfig.associatedForm?.formId;
		const formName = proformaReqTypeConfig.associatedForm?.formName;
		const markedForRemoval = proformaReqTypeConfig.associatedForm?.markedForRemoval;

		if (extraDefinerId !== null && formId && markedForRemoval) {
			return {
				cloudId,
				projectId,
				requestTypeId: extraDefinerId,
				formId,
				formName,
			};
		}
		return undefined;
	},
);

export const proformaReqTypeFormAddRequest = createSelector(
	getCloudId,
	getProjectId,
	getExtraDefinerId,
	getProformaReqTypeConfig,
	getProformaFormName,
	(
		cloudId,
		projectId,
		extraDefinerId,
		proformaReqTypeConfig,
		formName,
	): ProformaReqTypeFormAddRequest | undefined => {
		const formId = proformaReqTypeConfig.optimisticNewForm?.formId;
		if (extraDefinerId !== null && formId) {
			return {
				cloudId,
				projectId,
				requestTypeId: extraDefinerId,
				formId,
				formName,
			};
		}
		return undefined;
	},
);

export const isProformaReqTypeConfigSaving = (state: State) => state.isSaving;

export const isProformaReqTypeConfigLoading = (state: State) => state.isLoading;

export const getDuplicateJiraFields = (
	state: State,
	requestTypeLayoutItems: ActiveLayoutItem[],
): LinkedField[] => {
	const linkedJiraFieldsInForm = getProformaFormLinkedFields(state);

	if (isProformaFormUsedAsIssueForm(state)) {
		return [];
	}

	if (!linkedJiraFieldsInForm) {
		return [];
	}

	const requestTypeJiraFields = requestTypeLayoutItems.map((jiraField) => ({
		name: jiraField.data.name,
		key: jiraField.data.key,
	}));

	const duplicateJiraFieldsInForm = requestTypeJiraFields.filter(
		(field) => linkedJiraFieldsInForm.includes(field.key) && field.key !== 'summary',
	);

	return duplicateJiraFieldsInForm;
};
